import { useMemo } from 'react';
import { useAuthSessionStore } from '../stores/auth-session-store';
import { allowsAllPaths, allowsAnyPath, allowsPath } from './endpoint-access';
import { NO_PERMISSIONS } from './session-permissions';

/**
 * The permission names the current session holds.
 *
 * Fetched from `GET /rf-backend/staff-permissions` by `bootstrapAuth`, not decoded from the id
 * token — see `services/permission.service.ts` for why that distinction matters.
 */
export const useGrantedPermissions = (): ReadonlySet<string> =>
  useAuthSessionStore((state) => state.permissions);

export type UsePermissionResult = {
  /**
   * `false` until the session has been resolved one way or the other.
   *
   * Check this before hiding something. Permissions are empty while the session is `'unknown'`,
   * so a gate that ignores `isReady` hides every restricted control for the first frame and
   * then pops it in — and, worse, a route guard that ignores it bounces a signed-in user out.
   */
  isReady: boolean;
  /** Holds this permission NAME (e.g. `can_get_customers`). */
  has: (permission: string) => boolean;
  /** Holds at least one of these permission names. An empty list means no restriction. */
  hasAny: (permissions: readonly string[]) => boolean;
  /** Holds every one of these permission names. An empty list means no restriction. */
  hasAll: (permissions: readonly string[]) => boolean;
  /** May use this endpoint PATH. Unmapped paths deny — see `allowsPath`. */
  canUsePath: (path: string) => boolean;
  /** May use at least one of these endpoint paths. This is what a nav entry asks. */
  canUseAnyPath: (paths: readonly string[]) => boolean;
  /** May use every one of these endpoint paths. */
  canUseAllPaths: (paths: readonly string[]) => boolean;
  /** The raw set, for callers that need to do their own thing with it. */
  granted: ReadonlySet<string>;
};

/**
 * The permission check for RF Backend.
 *
 * This console has a real authorization model, unlike the sibling `rf-gdx-dashboard-v2` and
 * `rf-mls-vendor-dashboard-v2` workspaces whose `CLAUDE.md` files both say no such model exists.
 * Do not read those and conclude the same here. See CLAUDE.md > Authorization.
 *
 * Takes permission **names**, not endpoint paths, for `has`/`hasAny`/`hasAll` — the same
 * argument the legacy `usePermission().checkRoles([...])` took, so legacy call sites port
 * across unchanged:
 *
 * ```tsx
 * const { hasAll } = usePermission();
 * <Button disabled={!hasAll(['can_edit_published_agreements'])}>Save</Button>
 * ```
 *
 * Use `canUseAnyPath` where the caller naturally speaks in endpoints — nav entries and route
 * metadata.
 *
 * ### What this is not
 *
 * It is a UI affordance, not a security boundary. The backend enforces the same rules; hiding a
 * control here only stops a user being shown something that would fail. Never treat a passing
 * check as authorisation to skip a server-side one.
 */
export const usePermission = (): UsePermissionResult => {
  const status = useAuthSessionStore((state) => state.status);
  const permissionStatus = useAuthSessionStore((state) => state.permissionStatus);
  const granted = useGrantedPermissions();

  return useMemo(() => {
    // Both have to have resolved. The session alone is not enough: it settles locally from
    // Amplify while the grant is still in flight, and a check run in that window denies
    // everything.
    const isReady = status !== 'unknown' && permissionStatus !== 'unknown';
    // While the session is unresolved nothing is granted, so every check below denies. That is
    // deliberate: `isReady` is how a caller tells "not yet" apart from "not allowed".
    const active = isReady ? granted : NO_PERMISSIONS;

    return {
      isReady,
      granted: active,
      has: (permission: string) => active.has(permission),
      hasAny: (permissions: readonly string[]) =>
        permissions.length === 0 || permissions.some((permission) => active.has(permission)),
      hasAll: (permissions: readonly string[]) =>
        permissions.every((permission) => active.has(permission)),
      canUsePath: (path: string) => allowsPath(active, path),
      canUseAnyPath: (paths: readonly string[]) => allowsAnyPath(active, paths),
      canUseAllPaths: (paths: readonly string[]) => allowsAllPaths(active, paths),
    };
  }, [status, permissionStatus, granted]);
};
